import * as Yup from "yup";

const { object, string, addMethod } = require("yup");

/* eslint-disable func-names */

addMethod(string, "requiredIf", function (bool, message) {
  if (!bool) {
    return this.nullable();
  }

  return this.required(message || "Required");
});

/**
 * Validates the general information of a supplier based on the defined schema.
 *
 * @param {object} generalInfo - The general information to validate.
 * @param {string} generalInfo.supplier_name - The name of the supplier, required.
 * @param {string} generalInfo.supplier_description - The description of the supplier, required.
 * @param {number} generalInfo.supplier_status - The status of the supplier, required.
 * @param {string} [generalInfo.regex] - Optional regex pattern for order numbers.
 * @param {boolean} [generalInfo.order_notes] - Indicates if order notes are enabled.
 * @param {string} [generalInfo.order_notes_name] - Order notes name, required if order notes are enabled.
 * @param {boolean} [generalInfo.reports] - Indicates if reports are enabled.
 * @param {string} [generalInfo.reports_name] - Reports name, required if reports are enabled.
 * @param {boolean} [generalInfo.custom_page] - Indicates if a custom page is enabled.
 * @param {string} [generalInfo.custom_page_url] - Custom page URL, required if a custom page is enabled.
 * @param {string} generalInfo.logo_url - The URL of the logo, required and must be a valid URL.
 * @param {boolean} [generalInfo.sales_assist] - Indicates if sales assist is enabled.
 * @param {string} [generalInfo.sales_assist_text] - Optional text for sales assist.
 * @param {boolean} [generalInfo.agent_assist] - Indicates if agent support is enabled.
 * @param {string} [generalInfo.agent_assist_text] - Optional text for agent support.
 * @param {boolean} [generalInfo.leaderboard] - Indicates if leaderboard is enabled.
 * @param {string} [generalInfo.last_updated] - The last updated timestamp.
 * @returns {Promise<object>} A promise that resolves with the validated general information or rejects with an error message.
 */
export function validateGenInfo(generalInfo) {
  return new Promise((resolve, reject) => {
    const genInfoSchema = object().shape({
      supplier_name: Yup.string().required("Supplier name is required"),

      supplier_description: Yup.string().required(
        "Supplier description is required"
      ),

      supplier_status: Yup.number().required("Supplier status is required"),
      regex: Yup.string(),

      order_notes: Yup.boolean(),

      order_notes_name: Yup.string()
        .when("order_notes", {
          is: true,
          then: schema =>
            schema.required(
              "Order notes name is required when order notes is enabled"
            ),
          otherwise: schema => schema.notRequired(),
        })
        .nullable(),

      reports: Yup.boolean(),

      reports_name: Yup.string()
        .when("reports", {
          is: true,
          then: schema =>
            schema.required("Reports name is required when reports is enabled"),
          otherwise: schema => schema.notRequired(),
        })
        .nullable(),

      custom_page: Yup.boolean(),

      custom_page_url: Yup.string()
        .when("custom_page", {
          is: true,
          then: schema =>
            schema
              .required(
                "Custom page URL is required when custom page is enabled"
              )
              .url("Must be a valid URL"),
          otherwise: schema => schema.notRequired(),
        })
        .nullable(),

      logo_url: Yup.string()
        .required("Logo URL is required")
        .url("Must be a valid URL"),

      sales_assist: Yup.boolean(),

      sales_assist_text: Yup.string().nullable(),

      agent_assist: Yup.boolean(),

      agent_assist_text: Yup.string().nullable(),

      leaderboard: Yup.boolean(),

      last_updated: Yup.string(),
    });

    genInfoSchema
      .validate(generalInfo)
      .then(result => {
        resolve(result);
      })
      .catch(err => {
        reject(err.message);
      });
  });
}

/**
 * Validates an array of contact information objects.
 *
 * Each contact object must contain the following properties:
 * - contact_email: A valid email address, required.
 * - contact_first_name: The first name of the contact, required.
 * - contact_surname: The surname of the contact, required.
 * - contact_number: A valid SA phone number (e.g. 0821234567 or +27821234567), required.
 *
 * @param {Array<object>} contacts - An array of contact information objects to validate.
 * @returns {Promise<Array<object>>} A promise that resolves with the validated contact information array or rejects with an error message.
 */
export function validateContacts(contacts) {
  return new Promise((resolve, reject) => {
    const contactSchema = Yup.array().of(
      Yup.object().shape({
        contact_email: Yup.string()
          .email("Must be a valid email address")
          .required("Contact email is required"),

        contact_first_name: Yup.string().required("First name is required"),

        contact_surname: Yup.string().required("Surname is required"),

        contact_number: Yup.string()
          .matches(
            /^(0|\+27)\d{9}$/,
            "Must be a valid SA phone number (e.g., 0821234567 or +27821234567)"
          )
          .required("Contact number is required"),
      })
    );

    contactSchema
      .validate(contacts)
      .then(result => {
        resolve(result);
      })
      .catch(err => {
        reject(err.message);
      });
  });
}
